import * as React from 'react';
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";

import Button from '../Button/Button';
import SubmitButton from '../SubmitButton/SubmitButton';
import ExpenseRow from '../ExpenseRow/ExpenseRow';
import { FixedEvent } from '../../types/FixedEvent';
import { ReoccuringEvent } from '../../types/ReoccuringEvent';
import { Expense } from '../../types/Expense';

import type { Cycle } from '../../types/Cycle';

type Input = {
    name: string;
    amount: string;
    repeats: boolean;
    // repeats = true
    startDate?: string;
    endDate?: string;
    frequency?: string;
    cycle?: Cycle;

    // repeats = false
    date?: string;
};

type ExpenseManagementProps = {
    expenses: Expense[];
    setExpenses: (expenses: Expense[]) => void;
};

function ExpenseManagement({expenses, setExpenses}: ExpenseManagementProps) {

    const [add, setAdd] = React.useState<boolean>(false);
    const [repeats, setRepeats] = React.useState<boolean>(true);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Input>();

    const onSubmit: SubmitHandler<Input> = (data) => {
        createExpense(data);
        setAdd(false);
    };

    const createExpense = (data: Input) => {
        const expense: Expense = new Expense(
            data.name,
            parseFloat(data.amount),
            repeats ? new ReoccuringEvent(
                data.startDate ? new Date(data.startDate) : new Date(),
                data.endDate ? new Date(data.endDate) : new Date(),
                data.frequency ? parseInt(data.frequency) : 1,
                data.cycle ?? "daily",
            ) : new FixedEvent(
                data.date ? new Date(data.date) : new Date()
            )
        );

        expenses.push(expense);
        setExpenses([...expenses]);
    };

    const removeExpense = (index: number) => {
        expenses.splice(index, 1);
        setExpenses([...expenses]);
    };


    return (
        <div>
            <h3>Expenses</h3>
            {expenses.map((expense, index) => (
                <ExpenseRow key={expense.id} expense={expense} index={index} remove={removeExpense}></ExpenseRow>
            ))}
            <Button callback={() => { setAdd(true) }}>Add Expense</Button>
            { add && (
                <div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <label htmlFor="name">Name</label>
                        <input id="name" {...register("name", { required: true })} />
                        {errors.name && <span>This field is required</span>}

                        <label htmlFor="name">Amount</label>
                        <input id="amount" type="number" min={0} step={0.01} {...register("amount", { required: true })} />
                        {errors.amount && <span>This field is required</span>}

                        <label htmlFor="repeats">Repeats</label>
                        <input id="repeats" type="checkbox" checked={repeats} onChange={(e) => setRepeats(e.target.checked)} />

                        { repeats ? (
                            <div>
                                <label htmlFor="startDate">Start Date:</label>
                                <input id="startDate" type="date" {...register("startDate", { required: repeats })}></input>
                                {errors.startDate && <span>This field is required</span>}

                                <label htmlFor="endDate">End Date:</label>
                                <input id="endDate" type="date" {...register("endDate")}></input>

                                <label htmlFor="frequency">Repeats every:</label>
                                <input id="frequency" type="number" min={1} {...register("frequency", { required: repeats })}></input>
                                {errors.frequency && <span>This field is required</span>}

                                <label htmlFor="cycle">Repeats:</label>
                                <select id="cycle" {...register("cycle", { required: repeats })}>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                                {errors.cycle && <span>This field is required</span>}
                            </div>
                        ) : (
                            <div>
                                <label htmlFor="date">Date:</label>
                                <input id="date" type="date" {...register("date", { required: !repeats })}></input>
                                {errors.date && <span>This field is required</span>}
                            </div>
                        ) }
                        <Button callback={() => { setAdd(false) }}>Cancel</Button>
                        <SubmitButton>Add</SubmitButton>
                    </form>
                </div>
            )}
        </div>
    );
}


export default ExpenseManagement;
